/**
 * File: client/src/components/Overview/OverviewTab.js
 * ===================================================
 * SIMPLIFIED Property Overview - Clean & Scannable
 *
 * Sections:
 * 1. Property Snapshot (value, equity, mortgage)
 * 2. Homeowner Intent (strategy selector)
 * 3. Key Dates (mortgage renewal, insurance)
 * 4. Next Actions (top 3 tasks)
 */

import React, { useState } from "react";
import {
  Home,
  TrendingUp,
  TrendingDown,
  Calendar,
  DollarSign,
  Target,
  AlertCircle,
  ChevronRight,
  Building2,
  Wallet,
  PiggyBank,
  Key,
  ArrowUpRight,
} from "lucide-react";

/**
 * Format currency
 */
const formatCurrency = (value) => {
  if (!value) return "—";
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Calculate days until a date
 */
const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (isNaN(target.getTime())) return null;
  const today = new Date();
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
};

/**
 * Intent Options
 */
const INTENT_OPTIONS = [
  {
    id: 'hold',
    label: 'Hold & Maintain',
    icon: Home,
    description: 'Keep as primary residence',
    color: 'bg-blue-50 border-blue-200 text-blue-700'
  },
  {
    id: 'rent',
    label: 'Rent Out',
    icon: Key,
    description: 'Generate rental income',
    color: 'bg-green-50 border-green-200 text-green-700'
  },
  {
    id: 'sell',
    label: 'Sell Soon',
    icon: ArrowUpRight,
    description: 'List within 12 months',
    color: 'bg-amber-50 border-amber-200 text-amber-700'
  },
  {
    id: 'refinance',
    label: 'Refinance',
    icon: Wallet,
    description: 'Access equity or lower rate',
    color: 'bg-purple-50 border-purple-200 text-purple-700'
  },
  {
    id: 'paydown',
    label: 'Pay Down Faster',
    icon: PiggyBank,
    description: 'Accelerate mortgage payoff',
    color: 'bg-teal-50 border-teal-200 text-teal-700'
  },
  {
    id: 'equity_target',
    label: 'Equity Target',
    icon: Target,
    description: 'Sell when equity reaches goal',
    color: 'bg-rose-50 border-rose-200 text-rose-700'
  },
];

/**
 * Property Snapshot - The Big Numbers
 */
function PropertySnapshot({ property }) {
  const value = property.currentValue || property.valuation?.estimate || 0;
  const mortgageBalance = property.mortgage?.balance || 0;
  const equity = value - mortgageBalance;
  const equityPercent = value > 0 ? Math.round((equity / value) * 100) : 0;
  const monthlyPayment = property.mortgage?.monthlyPayment || 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      {/* Main Value */}
      <div className="text-center mb-6">
        <p className="text-sm text-gray-500 mb-1">Property Value</p>
        <p className="text-4xl font-bold text-gray-900">{formatCurrency(value)}</p>
        {property.valuation?.trend && (
          <div className={`inline-flex items-center gap-1 mt-2 text-sm ${
            property.valuation.trend === 'up' ? 'text-green-600' :
            property.valuation.trend === 'down' ? 'text-red-600' : 'text-gray-500'
          }`}>
            {property.valuation.trend === 'up' ? <TrendingUp className="w-4 h-4" /> :
             property.valuation.trend === 'down' ? <TrendingDown className="w-4 h-4" /> : null}
            <span className="capitalize">{property.valuation.trend} trend</span>
          </div>
        )}
      </div>

      {/* Equity Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-600">Your Equity</span>
          <span className="font-semibold text-gray-900">{formatCurrency(equity)} ({equityPercent}%)</span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all duration-500"
            style={{ width: `${equityPercent}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>Mortgage: {formatCurrency(mortgageBalance)}</span>
          <span>Equity: {formatCurrency(equity)}</span>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
        <div className="text-center">
          <p className="text-xs text-gray-500">Monthly Payment</p>
          <p className="text-lg font-semibold text-gray-900">{formatCurrency(monthlyPayment)}</p>
        </div>
        <div className="text-center border-x border-gray-100">
          <p className="text-xs text-gray-500">Rate</p>
          <p className="text-lg font-semibold text-gray-900">
            {property.mortgage?.rate ? `${property.mortgage.rate}%` : '—'}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500">Property Tax</p>
          <p className="text-lg font-semibold text-gray-900">
            {property.propertyTax ? formatCurrency(property.propertyTax) + '/yr' : '—'}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Homeowner Intent Selector
 */
function IntentSelector({ property, onIntentChange }) {
  const [selectedIntent, setSelectedIntent] = useState(property.intent || 'hold');
  const [equityTarget, setEquityTarget] = useState(property.equityTarget || '');
  const [showTargetInput, setShowTargetInput] = useState(selectedIntent === 'equity_target');

  const handleSelect = (intentId) => {
    setSelectedIntent(intentId);
    setShowTargetInput(intentId === 'equity_target');
    if (onIntentChange) {
      onIntentChange(intentId, intentId === 'equity_target' ? equityTarget : null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Target className="w-5 h-5 text-[#CC0202]" />
        <h3 className="text-lg font-semibold text-gray-900">What's Your Goal?</h3>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Your strategy helps us prioritize recommendations and alerts.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {INTENT_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isSelected = selectedIntent === option.id;

          return (
            <button
              key={option.id}
              onClick={() => handleSelect(option.id)}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                isSelected
                  ? option.color + ' border-current'
                  : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
            >
              <Icon className={`w-5 h-5 mb-2 ${isSelected ? '' : 'text-gray-400'}`} />
              <p className={`text-sm font-medium ${isSelected ? '' : 'text-gray-700'}`}>
                {option.label}
              </p>
              <p className={`text-xs mt-1 ${isSelected ? 'opacity-75' : 'text-gray-400'}`}>
                {option.description}
              </p>
            </button>
          );
        })}
      </div>

      {/* Equity Target Input */}
      {showTargetInput && (
        <div className="mt-4 p-4 bg-rose-50 rounded-xl">
          <label className="block text-sm font-medium text-rose-700 mb-2">
            Sell when equity reaches:
          </label>
          <div className="flex items-center gap-2">
            <span className="text-rose-600">$</span>
            <input
              type="number"
              value={equityTarget}
              onChange={(e) => {
                setEquityTarget(e.target.value);
                if (onIntentChange) onIntentChange('equity_target', e.target.value);
              }}
              placeholder="500,000"
              className="flex-1 px-3 py-2 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
            />
          </div>
          {property.currentValue && equityTarget && (
            <p className="text-xs text-rose-600 mt-2">
              Current equity: {formatCurrency((property.currentValue || 0) - (property.mortgage?.balance || 0))}
              {' → '}Target: {formatCurrency(Number(equityTarget))}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Key Dates - Simple Timeline
 */
function KeyDates({ property }) {
  const dates = [];

  // Mortgage renewal
  if (property.mortgage?.renewalDate) {
    const days = daysUntil(property.mortgage.renewalDate);
    dates.push({
      id: 'mortgage',
      label: 'Mortgage Renewal',
      date: property.mortgage.renewalDate,
      days,
      urgent: days !== null && days <= 90,
      icon: Building2,
    });
  }

  // Insurance renewal
  if (property.insurance?.renewalDate) {
    const days = daysUntil(property.insurance.renewalDate);
    dates.push({
      id: 'insurance',
      label: 'Insurance Renewal',
      date: property.insurance.renewalDate,
      days,
      urgent: days !== null && days <= 30,
      icon: DollarSign,
    });
  }

  if (dates.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="w-5 h-5 text-[#CC0202]" />
        <h3 className="text-lg font-semibold text-gray-900">Key Dates</h3>
      </div>

      <div className="space-y-3">
        {dates.map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.id}
              className={`flex items-center justify-between p-4 rounded-xl ${
                item.urgent ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <Icon className={`w-5 h-5 ${item.urgent ? 'text-amber-600' : 'text-gray-400'}`} />
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.label}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(item.date).toLocaleDateString('en-CA', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
              <div className={`text-right ${item.urgent ? 'text-amber-600' : 'text-gray-500'}`}>
                {item.days !== null && (
                  <>
                    {item.urgent && <AlertCircle className="w-4 h-4 inline mr-1" />}
                    <span className="text-sm font-medium">
                      {item.days <= 0 ? 'Due now' : `${item.days} days`}
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Next Actions Preview
 */
function NextActions({ property, onViewAll }) {
  // Sample tasks - in real app, fetch from API
  const tasks = [
    { id: 1, title: 'Schedule HVAC inspection', priority: 'high', dueIn: '2 weeks' },
    { id: 2, title: 'Test smoke detectors', priority: 'medium', dueIn: '1 month' },
    { id: 3, title: 'Clean gutters', priority: 'low', dueIn: '6 weeks' },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-[#CC0202]" />
          <h3 className="text-lg font-semibold text-gray-900">Next Actions</h3>
        </div>
        <button
          onClick={onViewAll}
          className="text-sm text-[#CC0202] font-medium hover:underline flex items-center gap-1"
        >
          View All <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-2">
        {tasks.map((task) => (
          <div
            key={task.id}
            className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${
                task.priority === 'high' ? 'bg-red-500' :
                task.priority === 'medium' ? 'bg-amber-500' : 'bg-green-500'
              }`} />
              <span className="text-sm text-gray-700">{task.title}</span>
            </div>
            <span className="text-xs text-gray-400">{task.dueIn}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Property Info Footer
 */
function PropertyInfo({ property }) {
  return (
    <div className="bg-gray-50 rounded-2xl p-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
        <div>
          <p className="text-gray-500 mb-1">Address</p>
          <p className="font-medium text-gray-900">{property.address || '—'}</p>
        </div>
        <div>
          <p className="text-gray-500 mb-1">Type</p>
          <p className="font-medium text-gray-900">{property.type || '—'}</p>
        </div>
        <div>
          <p className="text-gray-500 mb-1">Size</p>
          <p className="font-medium text-gray-900">
            {property.sqft ? `${property.sqft.toLocaleString()} sqft` : '—'}
          </p>
        </div>
        <div>
          <p className="text-gray-500 mb-1">Purchased</p>
          <p className="font-medium text-gray-900">
            {property.purchaseDate
              ? new Date(property.purchaseDate).toLocaleDateString('en-CA', { month: 'short', year: 'numeric' })
              : '—'}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * OverviewTab - Main Component
 */
const OverviewTab = ({ property }) => {
  const handleIntentChange = (intent, equityTarget) => {
    console.log('Intent changed:', intent, equityTarget);
    // TODO: Save to backend
  };

  const handleViewAllTasks = () => {
    // Switch to HomeRun tab
    console.log('View all tasks');
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 py-6">
      {/* Row 1: Snapshot + Intent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PropertySnapshot property={property} />
        <IntentSelector property={property} onIntentChange={handleIntentChange} />
      </div>

      {/* Row 2: Key Dates + Next Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <KeyDates property={property} />
        <NextActions property={property} onViewAll={handleViewAllTasks} />
      </div>

      {/* Row 3: Property Info */}
      <PropertyInfo property={property} />
    </div>
  );
};

export default OverviewTab;
